import { useCallback, useEffect, useMemo, useReducer, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';

import BrandLine from '@/components/layout/BrandLine/BrandLine';
import AddressSearchBar from '@/components/shirabe/AddressSearchBar/AddressSearchBar';
import SiteMap from '@/components/shirabe/SiteMap/SiteMap';
import SiteBrief, { type BriefView } from '@/components/shirabe/SiteBrief/SiteBrief';
import ChapterNav from '@/components/shirabe/ChapterNav/ChapterNav';
import { ChapterNumberingProvider } from '@/components/shirabe/ChapterSection/ChapterNumbering';
import VerdictCard from '@/components/shirabe/VerdictCard/VerdictCard';
import ActionBar from '@/components/shirabe/ActionBar/ActionBar';
import CandidateRail from '@/components/shirabe/CandidateRail/CandidateRail';
import CompareModal from '@/components/shirabe/CompareModal/CompareModal';
import AddressPicker from '@/components/shirabe/AddressPicker/AddressPicker';
import ChapterOverview from '@/components/shirabe/chapters/ChapterOverview';
import ChapterHazards from '@/components/shirabe/chapters/ChapterHazards';
import ChapterPrice from '@/components/shirabe/chapters/ChapterPrice';
import ChapterEnvironment from '@/components/shirabe/chapters/ChapterEnvironment';
import ChapterProspects from '@/components/shirabe/chapters/ChapterProspects';
import ChapterBuilding from '@/components/shirabe/chapters/ChapterBuilding';
import ChapterGround from '@/components/shirabe/chapters/ChapterGround';
import ChapterSummary from '@/components/shirabe/chapters/ChapterSummary';
import ConfirmDialog from '@/components/shirabe/ConfirmDialog/ConfirmDialog';
import Button from '@/components/ui/Button/Button';

import { api, ApiError } from '@/lib/api';
import type { GeocodeCandidate, Point, SiteAnalysisResponse } from '@/lib/api.types';
import { hasProspectsData, visibleChaptersFor } from '@/lib/chapters';
import { candidateStore, useCandidates } from '@/lib/candidate-store';
import { useActiveChapter } from '@/hooks/useActiveChapter';
import { scrollToId } from '@/lib/scroll';

import styles from './ReportWorkspace.module.css';

type Status = 'idle' | 'searching' | 'located' | 'analyzing' | 'ready' | 'error';

type State = {
  status: Status;
  point: Point | null;
  address: string | null;
  analysis: SiteAnalysisResponse | null;
  error: string | null;
};

type Action =
  | { type: 'START_GEOCODE' }
  | { type: 'GEOCODED'; point: Point; address: string }
  | { type: 'POINT_SELECTED'; point: Point; address: string | null }
  | { type: 'ADDRESS_RESOLVED'; point: Point; address: string }
  | { type: 'START_ANALYZE'; point: Point; address: string | null }
  | { type: 'ANALYZED'; analysis: SiteAnalysisResponse }
  | { type: 'FAILED'; error: string }
  | { type: 'RESET' };

const initialState: State = {
  status: 'idle',
  point: null,
  address: null,
  analysis: null,
  error: null,
};

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'START_GEOCODE':
      return { ...state, status: 'searching', error: null };
    case 'GEOCODED':
      return {
        ...state,
        status: 'located',
        point: action.point,
        address: action.address,
        analysis: null,
        error: null,
      };
    case 'POINT_SELECTED':
      return {
        ...state,
        status: 'located',
        point: action.point,
        address: action.address,
        analysis: null,
        error: null,
      };
    case 'ADDRESS_RESOLVED': {
      // 逆ジオコーディング結果は「同じ地点」かつ「住所未確定」のときだけ反映する。
      // 連続クリック時の古い応答や、文字検索で確定済みの住所の上書きを防ぐ。
      // status は見ない・変えない（analyzing 中に届いた住所もそのまま活かす）。
      if (!state.point || state.address !== null) return state;
      if (
        state.point.lat !== action.point.lat ||
        state.point.lon !== action.point.lon
      ) {
        return state;
      }
      return { ...state, address: action.address };
    }
    case 'START_ANALYZE':
      return {
        ...state,
        status: 'analyzing',
        point: action.point,
        address: action.address,
        error: null,
      };
    case 'ANALYZED':
      return {
        ...state,
        status: 'ready',
        analysis: action.analysis,
        address: action.analysis.address ?? state.address,
        error: null,
      };
    case 'FAILED':
      return { ...state, status: 'error', error: action.error };
    case 'RESET':
      // ロゴ＝ホームで最初の検索画面へ戻す。地点・住所・結果をまとめて初期状態に。
      return initialState;
    default:
      return state;
  }
}

function errorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    return `バックエンドからの応答エラー (${e.status})。サーバが起動しているか、API Base を確認してください。`;
  }
  if (e instanceof Error) {
    return e.message;
  }
  return '通信に失敗しました。';
}

// 立地レポートの作業画面 (住所検索 → 地図 → 解析 → 章 → 操作)。C 向け (pages/Shirabe) と
// B 向けパートナー版 (pages/PartnerReport) が同じ本体を使い、ヘッダー/フッター/文言/追加操作
// (PDF 出力など) だけを差し替える。状態機械と導線はここに一本化しておく。
export type ReportWorkspaceProps = {
  /** ページ上部のヘッダー。onHome を受け取り、ロゴ＝最初の検索画面へ戻す操作に配線する。 */
  renderHeader: (ctx: { onHome: () => void }) => ReactNode;
  footer: ReactNode;
  hero: { eyebrow: string; title: string };
  /** 成果物名。概要カードの見出しに出す。省略時は「立地レポート」。 */
  reportTitle?: string;
  /** レポート末尾、共有/保存/比較の ActionBar の直前に差し込む変種固有の操作 (PDF 出力など)。 */
  renderExtraActions?: (ctx: { analysis: SiteAnalysisResponse }) => ReactNode;
};

// Phase messages shown (and cycled) while the analyze request runs, roughly
// mirroring the order the backend gathers data so the wait feels like progress.
const ANALYZE_STEPS = [
  '災害リスクを確認しています',
  '地盤の特徴を調べています',
  '価格の相場を集めています',
  '周辺の暮らし環境を調べています',
  'レポートをまとめています',
];

export default function ReportWorkspace({
  renderHeader,
  footer,
  hero,
  reportTitle,
  renderExtraActions,
}: ReportWorkspaceProps) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [searchParams, setSearchParams] = useSearchParams();
  const [compareOpen, setCompareOpen] = useState(false);
  // 「結果のリンクをコピー」直後の通知用フラグ。
  const [copied, setCopied] = useState(false);
  const [pendingChange, setPendingChange] = useState<
    | { type: 'search'; query: string }
    | { type: 'mapClick'; point: Point }
    | null
  >(null);
  const [geoCandidates, setGeoCandidates] = useState<GeocodeCandidate[] | null>(null);
  const candidates = useCandidates();

  const runAnalyze = useCallback(
    async (point: Point, address: string | null) => {
      dispatch({ type: 'START_ANALYZE', point, address });
      try {
        const analysis = await api.analyze({ point, address });
        dispatch({ type: 'ANALYZED', analysis });
      } catch (e) {
        dispatch({ type: 'FAILED', error: errorMessage(e) });
      }
    },
    [],
  );

  const runSearch = useCallback(
    async (query: string) => {
      dispatch({ type: 'START_GEOCODE' });
      setGeoCandidates(null);
      try {
        const res = await api.geocode(query, 5);
        const candidate = res.candidates[0];
        if (!candidate) {
          dispatch({ type: 'FAILED', error: '住所候補が見つかりませんでした。' });
          return;
        }
        // Provisionally select the closest match so the flow never stalls; if the
        // address was ambiguous, offer the others via the picker.
        dispatch({
          type: 'GEOCODED',
          point: candidate.point,
          address: candidate.title || query,
        });
        if (res.candidates.length > 1) {
          setGeoCandidates(res.candidates);
        }
      } catch (e) {
        dispatch({ type: 'FAILED', error: errorMessage(e) });
      }
    },
    [],
  );

  const handlePickCandidate = useCallback((candidate: GeocodeCandidate) => {
    dispatch({
      type: 'GEOCODED',
      point: candidate.point,
      address: candidate.title,
    });
    setGeoCandidates(null);
  }, []);

  const handleMapClick = useCallback(
    (point: Point) => {
      if (state.analysis) {
        setPendingChange({ type: 'mapClick', point });
        return;
      }
      dispatch({ type: 'POINT_SELECTED', point, address: null });
    },
    [state.analysis],
  );

  // Auto-locate from URL params (?q= or ?lat= & ?lon=). Analyze is NOT auto-run;
  // the user must press the "調査開始" button.
  useEffect(() => {
    const q = searchParams.get('q');
    const lat = searchParams.get('lat');
    const lon = searchParams.get('lon');
    if (q && !state.analysis && state.status === 'idle') {
      void runSearch(q);
      return;
    }
    if (lat && lon && !state.analysis && state.status === 'idle') {
      const latN = Number(lat);
      const lonN = Number(lon);
      if (Number.isFinite(latN) && Number.isFinite(lonN)) {
        const point = { lat: latN, lon: lonN };
        dispatch({ type: 'POINT_SELECTED', point, address: null });
        // 共有リンク(?lat&lon)で着地したら解析まで自動実行し、受け手がそのまま結果を見られるように。
        void runAnalyze(point, null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 地点だけ決まって住所が無いとき（地図クリック・?lat&lon 直リンク）、GSI 逆ジオコーダで
  // 住所を自動補完する。失敗・海上では何もしない（現状どおり緯度経度表示にフォールバック）。
  useEffect(() => {
    if (!state.point || state.address !== null) return;
    const point = state.point;
    let cancelled = false; // StrictMode の二重実行・地点変更後の古い応答を無効化
    void api
      .reverseGeocode(point)
      .then((res) => {
        if (cancelled || !res.address) return;
        dispatch({ type: 'ADDRESS_RESOLVED', point, address: res.address });
      })
      .catch(() => {
        /* 静かに緯度経度表示のまま */
      });
    return () => {
      cancelled = true;
    };
  }, [state.point, state.address]);

  const handleStartAnalyze = useCallback(() => {
    if (!state.point) return;
    void runAnalyze(state.point, state.address);
  }, [runAnalyze, state.point, state.address]);

  const handleSaveCandidate = useCallback(() => {
    if (!state.point) return;
    const label =
      state.address ||
      `候補地 (${state.point.lat.toFixed(3)}, ${state.point.lon.toFixed(3)})`;
    candidateStore.add({
      label,
      address: state.address,
      point: state.point,
    });
  }, [state.address, state.point]);

  // レポート結果は URL で共有する。地点(lat/lon)だけのリンクにし、?q を付けないことで受け手側の
  // 再ジオコーディングによる地点ズレを防ぐ。受け手は着地時に自動解析される(初回 useEffect)。
  // クリップボードAPIが使えない環境では一時 textarea + execCommand にフォールバックする。
  const handleShare = useCallback(async () => {
    if (!state.point) return;
    const url = new URL(window.location.href);
    url.search = '';
    url.searchParams.set('lat', String(state.point.lat));
    url.searchParams.set('lon', String(state.point.lon));
    const shareUrl = url.toString();
    try {
      await navigator.clipboard.writeText(shareUrl);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = shareUrl;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
      } catch {
        /* コピー不可環境では黙ってあきらめる */
      }
      document.body.removeChild(ta);
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  }, [state.point]);

  const applyNewSearch = useCallback(
    (query: string) => {
      const next = new URLSearchParams(searchParams);
      next.set('q', query);
      next.delete('lat');
      next.delete('lon');
      setSearchParams(next, { replace: false });
      void runSearch(query);
    },
    [runSearch, searchParams, setSearchParams],
  );

  const handleNewSearch = useCallback(
    (query: string) => {
      if (state.analysis) {
        setPendingChange({ type: 'search', query });
        return;
      }
      applyNewSearch(query);
    },
    [applyNewSearch, state.analysis],
  );

  // ロゴ＝ホーム。どの導線（住所検索・?lat&lon 直リンク・地図クリック）からでも
  // 結果をクリアして最初の検索画面（先頭）へ戻す。URL からの推論では地図クリック導線を
  // 拾えないため、命令的にリセットする。
  const handleHome = useCallback(() => {
    dispatch({ type: 'RESET' });
    setGeoCandidates(null);
    setPendingChange(null);
    setSearchParams({}, { replace: false }); // ?q/?lat/?lon を消す
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [setSearchParams]);

  const confirmLocationChange = useCallback(() => {
    if (!pendingChange) return;
    if (pendingChange.type === 'search') {
      applyNewSearch(pendingChange.query);
    } else {
      dispatch({ type: 'POINT_SELECTED', point: pendingChange.point, address: null });
    }
    setPendingChange(null);
  }, [applyNewSearch, pendingChange]);

  const cancelLocationChange = useCallback(() => {
    setPendingChange(null);
  }, []);

  // 見通し章はデータがある時だけ出す (判定と章の並びは lib/chapters.ts、PDF 冊子と共有)。
  const hasProspects = state.analysis ? hasProspectsData(state.analysis) : false;
  const visibleChapters = useMemo(() => visibleChaptersFor(state.analysis), [state.analysis]);
  const chapterIds = useMemo(
    () => (state.analysis ? visibleChapters.map((c) => c.id) : []),
    [state.analysis, visibleChapters],
  );
  const activeId = useActiveChapter(chapterIds);

  // analyzing では同じカード位置の進捗へ、ready ではミニ結論へ視線を運ぶ。地図の下に
  // 静かに結果が描かれて「何も起きなかった」と誤解されるのを防ぐ（結論ファースト）。
  useEffect(() => {
    if (state.status === 'analyzing' || state.status === 'ready') {
      scrollToId('brief');
    }
  }, [state.status]);

  const isLoading = state.status === 'searching' || state.status === 'analyzing';

  // 概要カード（SiteBrief）の表示状態。地図横の同じ位置で idle→located→analyzing→ready と
  // 中身だけが変わるので、ユーザーは「どこを見ればよいか」に迷わない。
  const briefView: BriefView =
    state.status === 'ready'
      ? 'ready'
      : state.status === 'analyzing'
        ? 'analyzing'
        : state.point
          ? 'located'
          : 'idle';

  return (
    <>
      {renderHeader({ onHome: handleHome })}
      <main className={styles.main} id="top">
        <section className={styles.heroStrip} aria-labelledby="report-hero-title">
          <div className={styles.heroInner}>
            <div className={styles.heroText}>
              <div className={styles.eyebrow}>{hero.eyebrow}</div>
              <h1 id="report-hero-title" className={styles.title}>
                {hero.title}
              </h1>
            </div>
            <div className={styles.searchWrap}>
              <AddressSearchBar
                initialValue={searchParams.get('q') ?? ''}
                loading={isLoading}
                error={state.error}
                onSubmit={handleNewSearch}
              />
            </div>
          </div>
        </section>

        <section className={styles.mapRow}>
          <div className={styles.mapWrap}>
            <SiteMap
              point={state.point}
              onClick={handleMapClick}
            />
          </div>
          <SiteBrief
            view={briefView}
            address={state.address}
            point={state.point}
            analysis={state.analysis}
            steps={ANALYZE_STEPS}
            reportTitle={reportTitle}
            onStart={handleStartAnalyze}
            onSeeDetails={() => scrollToId('report')}
          />
        </section>

        {/* スマホで親指の届く位置に常設する開始ボタン（デスクトップは CSS で非表示。
            票内 CTA を補完し、地図の下までスクロールしなくても調査を始められる）。 */}
        {briefView === 'located' && (
          <div className={styles.mobileCta} role="region" aria-label="調査の開始">
            <Button
              variant="cta"
              size="lg"
              className={styles.mobileCtaBtn}
              onClick={handleStartAnalyze}
            >
              この地点で調査を開始
            </Button>
          </div>
        )}

        {state.analysis && (
          <div className={styles.reportLayout} id="report">
            <div className={styles.navRail}>
              <ChapterNav chapters={visibleChapters} activeId={activeId} />
            </div>
            <div className={styles.chapters}>
              <BrandLine />
              <VerdictCard analysis={state.analysis} />
              {/* 章の並びは lib/chapters.ts の CHAPTERS と一致させること(番号はそこから導出)。 */}
              <ChapterNumberingProvider chapters={visibleChapters}>
                <ChapterOverview analysis={state.analysis} />
                <ChapterHazards portfolio={state.analysis.hazards} site={state.analysis.site} />
                <ChapterGround ground={state.analysis.ground} site={state.analysis.site} />
                <ChapterBuilding portfolio={state.analysis.planning} site={state.analysis.site} />
                <ChapterPrice portfolio={state.analysis.price} site={state.analysis.site} />
                <ChapterEnvironment portfolio={state.analysis.life_environment} site={state.analysis.site} />
                {hasProspects && (
                  <ChapterProspects
                    portfolio={state.analysis.life_environment}
                    planning={state.analysis.planning}
                    site={state.analysis.site}
                  />
                )}
                <ChapterSummary analysis={state.analysis} />
              </ChapterNumberingProvider>

              {renderExtraActions?.({ analysis: state.analysis })}
              <ActionBar
                canAct={Boolean(state.analysis)}
                copied={copied}
                onShare={handleShare}
                onSave={handleSaveCandidate}
                onOpenCompare={() => setCompareOpen(true)}
                candidateCount={candidates.length}
              />
            </div>
          </div>
        )}

        <section className={styles.railWrap}>
          <CandidateRail onCompare={() => setCompareOpen(true)} />
        </section>
      </main>
      {footer}

      <CompareModal open={compareOpen} onClose={() => setCompareOpen(false)} />
      <ConfirmDialog
        open={pendingChange !== null}
        title="地点を変更しますか？"
        message="新しい地点に切り替えると、いま表示中のレポートは消えます。"
        confirmLabel="変更する"
        cancelLabel="キャンセル"
        onConfirm={confirmLocationChange}
        onCancel={cancelLocationChange}
      />
      <AddressPicker
        open={geoCandidates !== null}
        candidates={geoCandidates ?? []}
        onSelect={handlePickCandidate}
        onClose={() => setGeoCandidates(null)}
      />
    </>
  );
}
